import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
} from '@angular/core';
import { formatCountdownTo } from '@app/helpers/formatDateTime.helper';
import type { InvestmentRowActionType } from '@app/helpers/status.helper';
import { ButtonComponent } from '@app/ui-components/button/button.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import type { ICellRendererAngularComp } from 'ag-grid-angular';
import type { ICellRendererParams } from 'ag-grid-community';

export type InvestmentActionRow = {
  id: string;
  actionType: InvestmentRowActionType;
  pendingUntilRaw?: string | null;
};

export type InvestmentActionCellParams =
  ICellRendererParams<InvestmentActionRow> & {
    onAccept?: (row: InvestmentActionRow) => void;
    onReject?: (row: InvestmentActionRow) => void;
    onApprove?: (row: InvestmentActionRow) => void;
  };

@Component({
  selector: 'app-investment-action-cell-renderer',
  standalone: true,
  imports: [CommonModule, ButtonComponent, TranslateModule],
  templateUrl: './investment-action-cell-renderer.component.html',
  styleUrl: './investment-action-cell-renderer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InvestmentActionCellRendererComponent
  implements ICellRendererAngularComp, OnDestroy
{
  protected readonly translate = inject(TranslateService);
  private readonly cdr = inject(ChangeDetectorRef);

  actionType: InvestmentRowActionType = null;
  acceptLabel = '';
  rejectLabel = '';
  approveLabel = '';
  protected pendingCountdownText = '';
  private row: InvestmentActionRow | null = null;
  private onAccept?: (row: InvestmentActionRow) => void;
  private onReject?: (row: InvestmentActionRow) => void;
  private onApprove?: (row: InvestmentActionRow) => void;
  private countdownIntervalId: ReturnType<typeof setInterval> | null = null;

  agInit(params: InvestmentActionCellParams): void {
    this.clearCountdownInterval();
    this.row = params.data ?? null;
    this.actionType = this.row?.actionType ?? null;
    this.onAccept = params.onAccept;
    this.onReject = params.onReject;
    this.onApprove = params.onApprove;
    this.acceptLabel = this.translate.instant('admin.allProjects.actions.accept');
    this.rejectLabel = this.translate.instant('admin.allProjects.actions.reject');
    this.approveLabel = this.translate.instant(
      'admin.allProjects.actions.approve'
    );
    if (this.actionType === 'pending_countdown' && this.row?.pendingUntilRaw) {
      this.tickCountdown();
      this.countdownIntervalId = setInterval(() => {
        this.tickCountdown();
        this.cdr.markForCheck();
      }, 1000);
    }
  }

  ngOnDestroy(): void {
    this.clearCountdownInterval();
  }

  refresh(): boolean {
    return false;
  }

  private tickCountdown(): void {
    this.pendingCountdownText = this.row?.pendingUntilRaw
      ? formatCountdownTo(this.row.pendingUntilRaw)
      : '';
  }

  private clearCountdownInterval(): void {
    if (this.countdownIntervalId !== null) {
      clearInterval(this.countdownIntervalId);
      this.countdownIntervalId = null;
    }
  }

  protected get hasActions(): boolean {
    return this.actionType !== null && this.row !== null;
  }

  protected get showAcceptReject(): boolean {
    return this.actionType === 'accept_reject';
  }

  protected get showApprove(): boolean {
    return this.actionType === 'approve';
  }

  protected get showPendingCountdown(): boolean {
    return this.actionType === 'pending_countdown';
  }

  protected handleAccept(): void {
    if (this.row) this.onAccept?.(this.row);
  }

  protected handleReject(): void {
    if (this.row) this.onReject?.(this.row);
  }

  protected handleApprove(): void {
    if (this.row) this.onApprove?.(this.row);
  }
}
